package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/fatal_mistake")
@Composable
fun FatalMistakePage() {
    BlogLayout(
        PageProperties(
            "Coroutines Fatal Mistake",
            ""),
        BlogProperties("Coroutines", "Fatal Mistakes")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Kotlin Coroutines have become an essential part of our daily workflow. They make asynchronous programming and concurrency handling feel effortless — almost too effortless. But that simplicity can be deceptive. Many developers unknowingly fall into subtle yet dangerous pitfalls that, at first glance, seem like best practices.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Calling suspend Function Directly in Views",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Calling a suspend function directly inside a ViewModel may seem harmless, but it can lead to serious lifecycle issues and main thread blocking problems if not handled properly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is especially common in MVP (Model-View-Presenter) architecture, where developers try to fetch data and update UI state directly from a suspend function inside ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Wrong Approach
                    class MyViewModel(private val repository: MyRepository) : ViewModel() {
                       suspend fun fetchUserData() {
                            val user = repository.getUser() ❌ 
                        }
                    }
                    
                    //Correct Approach
                    class MyViewModel(private val repository: MyRepository) : ViewModel() {

                        private val _user = MutableLiveData<User>()
                        val user: LiveData<User> get() = _user

                        fun fetchUserData() {
                            viewModelScope.launch {
                                try {
                                    val result = repository.getUser() // ✅ Safe coroutine call
                                    _user.postValue(result)  // ✅ Updates UI on the Main thread
                                } catch (e: Exception) {
                                    // Handle error properly
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Incorrect use of Global scope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Wrong Approach
                    GloablScope.launch{
                      delay(400)
                      Log.d("Global scope", "Task Successful")
                    }
                    
                    //Correct Approach
                    viewmodelscope.launch{
                      delay(400)
                      Log.d("viewmodel", "Task Successful")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Fetching Data Sequentially Instead of Asynchronously",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Wrong Approach
                    suspend fun mistakeGetCarNames(ids: List<Int>): List<String> {
                        val names = mutableListOf<String>()
                        for (id in ids) {
                            names.add(getCarNameById(id)) ❌ // Wrong: Calls run one after another, wasting time!
                        }
                        return names
                    }
                    
                    //Correct Approach
                    suspend fun getCarNames(ids: List<Int>): List<String> {
                        return coroutineScope { 
                            ids.map { id -> 
                                async { getCarNameById(id) } // ✅ Runs all requests in parallel!
                            }.awaitAll() // ✅ Waits for all coroutines to complete and returns results
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Catching CancellationException in Suspend Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     //Wrong Approach
                     suspend fun mistakeRiskyTask() {
                         try {
                             delay(3_000L) // Simulating long-running operation
                             val error = 10 / 0  // ❌ This will throw an ArithmeticException
                         } catch (e: Exception) {
                             println("Code failed") ❌ // Catching all exceptions, including CancellationException!
                         }
                     }
                     
                    //Correct Approach
                    suspend fun riskyTask() {
                        try {
                            delay(3_000L) // Simulating long-running operation
                            val error = 10 / 0  // ❌ This will throw an ArithmeticException
                        } catch (e: Exception) {
                            if (e is CancellationException) throw e // ✅ Rethrow CancellationException!
                            println("Code not working") // ✅ Handle other exceptions properly
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Checking for coroutine cancellation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     //Wrong Approach
                    suspend fun mistakeDoSomething() {
                        val job = CoroutineScope(Dispatchers.Default).launch {
                            var random = Random.nextInt(100_000)
                            while (random != 50_000) {  // ❌ No check for cancellation
                                println("Random: ${'$'}random")
                                random = Random.nextInt(100_000)
                            }
                        }
                        println("Random: our job cancelled")
                        delay(500L)
                        job.cancel() // ❌ This won't immediately stop the loop
                    }
                    
                    //Correct Approach
                    suspend fun doSomethingWithIsActive() {
                        val job = CoroutineScope(Dispatchers.Default).launch {
                            var random = Random.nextInt(100_000)
                            while (isActive && random != 50_000) { // ✅ Manually check if coroutine is active
                                println("Random: ${'$'}random")
                                random = Random.nextInt(100_000)
                            }
                        }
                        println("Random: our job cancelled")
                        delay(500L)
                        job.cancel() // ✅ Coroutine will now stop properly
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Prefer withContext Over async for Single Operations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    suspend fun fetchData(): Data = withContext(Dispatchers.IO) {  
                        // Network call or DB operation  
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Handle Exceptions Gracefully",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val handler = CoroutineExceptionHandler { _, exception ->  
                        Log.e("CoroutineError", "Caught: ${'$'}exception")  
                    }  
                    
                    viewModelScope.launch(handler) {  
                        // Risky code here  
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "SupervisorJob for Independent Child Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Custom Kotlin Coroutine Races
                    
                    suspend fun slow(): String {
                      delay(1000)
                      return "This function is slow 🐌"
                    }

                    suspend fun fast(): String {
                      delay(10)
                      return "This function is fast 🚀"
                    }

                    suspend fun <T> race(vararg tasks: suspend () -> T): T =
                      channelFlow { tasks.forEach { launch { send(it()) } } }.first()

                    suspend fun main() {
                      val result = race(::slow, ::fast)
                      println(result) // → "This function is fast 🚀"
                    }
                    
                    suspend fun slow(): String? {
                      delay(1000)
                      return "This function is slow 🐌"
                    }

                    suspend fun fast(): String? {
                      delay(10)
                      return null
                    }

                    suspend fun <T> raceNotNull(vararg tasks: suspend () -> T): T =
                      channelFlow { tasks.forEach { launch { send(it()) } } }.first { it != null }

                    suspend fun main() {
                      val result = raceNotNull(::slow, ::fast)
                      println(result) // → "This function is slow 🐌"
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://emmanueldav.medium.com/5-fatal-coroutine-mistakes-nobody-talks-about-in-android-5e8932f91228